'use client';

import styles from './StripeCardPurchaseComponent.module.scss';
import FormStatus from '@components/FormComponents/FormStatus';
import StripeFormStatusTranslator from '@components/StripeElements/StripeFormStatusTranslator';
import logClientFailedPayment from '@lib/serverActions/logClientFailedPayment';
import logClientRedirectedPaymentStatus from '@lib/serverActions/logClientRedirectedPaymentStatus';
import { FormStatusData, PaymentIntentData, PurchaseData } from '@lib/types';
import {
    PaymentElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import { StripePaymentElementOptions } from '@stripe/stripe-js';
import { Dispatch, SetStateAction, useEffect } from 'react';
import GiftCardSummary from '../GiftCardSummary';
import getPurchasedDataAction from '@lib/serverActions/getPurchasedDataAction';
import Button from '@components/SectionComponents/Buttons/Button';
import { gtmGiftCardPurchaseEvent } from '@lib/gtm_events/GTMGiftCardPurchase';

const StripeCardPurchaseComponent = ({
    isLoading,
    setIsLoading,
    formStatusData,
    setFormStatusData,
    paymentStatus,
    setPaymentStatus,
    paymentIntentData,
    purchaseData,
    setPurchaseData,
}: {
    isLoading: boolean;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    formStatusData: FormStatusData;
    setFormStatusData: Dispatch<SetStateAction<FormStatusData>>;
    paymentStatus: 'incomplete' | 'complete';
    setPaymentStatus: Dispatch<SetStateAction<'incomplete' | 'complete'>>;
    paymentIntentData: PaymentIntentData;
    purchaseData: PurchaseData;
    setPurchaseData: Dispatch<SetStateAction<PurchaseData>>;
}) => {
const stripe = useStripe();
const elements = useElements();

    const paymentElementOptions: StripePaymentElementOptions = {
        layout: 'accordion',
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!stripe || !elements) {
            // Stripe.js hasn't yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        setIsLoading(true);

        // Fire GTM purchase event
        // Note: This will fire even if the payment fails at this stage.
        // All other options sucked and could cause duplicate purchase events.
        // We just need to know that purchases events on GA4 include failed payments.
        gtmGiftCardPurchaseEvent(
            purchaseData.amount,
            purchaseData.gift,
        );

        const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                // Make sure this is the payment completion page
                return_url: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_RETURN_URL!,
            },
        });

        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (error.type === 'card_error' || error.type === 'validation_error') {
            logClientFailedPayment(
                `client_${error.type}`,
                paymentIntentData.paymentIntentId,
            ); // log failed payment on client

            // update form status with failed payment error and message
            setFormStatusData({
                message: error.message
                    ? error.message
                    : 'Crikey! An unexplained error occurred.',
                show: true,
                colour: 'orange',
            });
        } else {
            setFormStatusData({
                message: 'Crikey! An unexpected error occurred.',
                show: true,
                colour: 'red',
            });
        }

        setIsLoading(false);
    };

    // If the payment intent data is available, retrieve the payment intent
    // and update the form status and purchase data accordingly.
    useEffect(() => {
        if (!stripe) {
            return;
        }

        if (!paymentIntentData.clientSecret) {
            return;
        }

        if (!paymentIntentData.fromParams) {
            return;
        }

        stripe
            .retrievePaymentIntent(paymentIntentData.clientSecret)
            .then(async ({ paymentIntent }) => {
                if (!paymentIntent) {
                    return;
                }

                // Get purchase data from the database to populate the summary section
                await getPurchasedDataAction(paymentIntent.id).then(
                    (response: PurchaseData | undefined) => {
                        if (response) {
                            response.amount = response.amount / 100; // Convert amount to dollars
                            setPurchaseData(response);
                        } else {
                            console.log('Error: No valid purchase data returned');
                        }
                    },
                );

                // do this in the background without awaiting
                logClientRedirectedPaymentStatus(
                    `client_${paymentIntent.status}`,
                    paymentIntent.id,
                );

                setFormStatusData(
                    StripeFormStatusTranslator(paymentIntent.status),
                );

                if (paymentIntent.status === 'succeeded') {
                    setPaymentStatus('complete');
                    setIsLoading(false);
                }
            });
    }, [
        paymentIntentData,
        setFormStatusData,
        setIsLoading,
        setPaymentStatus,
        stripe,
        setPurchaseData,
    ]);

    return (
        <div>
            
            <FormStatus {...formStatusData} />
            <GiftCardSummary purchaseData={purchaseData} />
            {paymentStatus === 'incomplete' && (
                <>
                <br />
                <form id="payment-form" className={styles.paymentForm} onSubmit={handleSubmit}>
                    <PaymentElement
                        id="payment-element"
                        options={paymentElementOptions}
                        onReady={() => {
                            setIsLoading(false);
                        }}
                    />
                    <Button
                        style={{ marginTop: '1.5rem' }}
                        type="submit"
                        disabled={isLoading || !stripe || !elements}
                        id="submit"
                    >
                        PAY NOW 
                    </Button>
                </form>
                </>
            )}
        </div>
    );
};

export default StripeCardPurchaseComponent;
